import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LlmParams, LlmTouchpointSlug } from "@gb/admin-contracts";
import { api, errorMessage } from "../../api/client";

export const promptKeys = {
  all: ["prompts"] as const,
  detail: (slug: string) => [...promptKeys.all, slug] as const,
  versions: (slug: string) => [...promptKeys.all, slug, "versions"] as const,
};

export function useTouchpoints() {
  return useQuery({
    queryKey: promptKeys.all,
    queryFn: async () => {
      const res = await api.prompts.listTouchpoints();
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.touchpoints;
    },
  });
}

export function useTouchpoint(slug: LlmTouchpointSlug) {
  return useQuery({
    queryKey: promptKeys.detail(slug),
    queryFn: async () => {
      const res = await api.prompts.getTouchpoint({ params: { slug } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function useVersions(slug: LlmTouchpointSlug) {
  return useQuery({
    queryKey: promptKeys.versions(slug),
    queryFn: async () => {
      const res = await api.prompts.listVersions({ params: { slug } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.versions;
    },
  });
}

export function useCreateVersion(slug: LlmTouchpointSlug) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { body: string; params: LlmParams; note?: string }) => {
      const res = await api.prompts.createVersion({ params: { slug }, body: input });
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body.version;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: promptKeys.all }),
  });
}

export function useActivate(slug: LlmTouchpointSlug) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (versionId: string | null) => {
      const res = await api.prompts.activate({ params: { slug }, body: { versionId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: promptKeys.all }),
  });
}

export function usePreview(slug: LlmTouchpointSlug) {
  return useMutation({
    mutationFn: async (input: { body: string; params?: LlmParams }) => {
      const res = await api.prompts.preview({ params: { slug }, body: input });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}
